import { useState } from 'react';
import { fill, formatDateOnly, monthWords, t, type DateFormat, type JobMergeDates, type JobRepin, type RecordSuggestionRow } from '@bms2/shared';
import { Checkbox, RadioGroup } from '../../components/controls';
import { FormFrame } from '../../components/forms/FormFrame';
import styles from './RecordSuggestions.module.css';

/**
 * 🔒 A Job History Merge's window (piece 13; decision 6440): Keep Dates leaves
 * the employment as it stands and Modify Dates writes the entry's months, but no
 * day another employment holds. The window names the months Modify Dates leaves
 * alone, and nothing is written until Merge. 🔒 Decision 6442: where Modify
 * Dates would end an open employment, a second step says the person is declared
 * no longer at the company and names the day, and its Confirm stays greyed
 * until the person ticks that; Cancel writes nothing. 🔒 Decision 6444: on a
 * row whose employment moved to another company, Re-pin comes first, and the
 * chosen employment's own Merge follows as usual. 🔒 Each step draws through
 * the Form frame (decision 3060). A choice of dates, a tick or an employment is
 * not typed, and Cancel writes nothing, so no step asks before it closes.
 */
export interface JobMergeWindowProps {
  row: RecordSuggestionRow;
  dateFormat: DateFormat;
  onCancel: () => void;
  /** The body the row's Accept posts. */
  onMerge: (body: JobMergeBody) => Promise<void> | void;
}

/** What a Merge posts: the dates chosen, the end confirmed, and the employment Re-pin chose. */
export type JobMergeBody = {
  jobDates?: JobMergeDates;
  endsEmployment?: true;
  pin?: string;
};

export function JobMergeWindow({ row, dateFormat, onCancel, onMerge }: JobMergeWindowProps) {
  const [dates, setDates] = useState<JobMergeDates | null>(null);
  // 🔒 Decision 7330: a retirement asks only the second step, the confirmation that ends the open employment.
  const retiring = row.jobEntry?.kind === 'Retired';
  const [step, setStep] = useState<'repin' | 'dates' | 'end'>(row.repins ? 'repin' : retiring ? 'end' : 'dates');
  const [ticked, setTicked] = useState(false);
  const [picked, setPicked] = useState<string | null>(null);
  const [repin, setRepin] = useState<JobRepin | null>(null);
  const pin = repin ? { pin: repin.employmentId } : {};
  if (step === 'repin' && row.repins) {
    const offered = row.repins;
    const go = () => {
      const chosen = offered.find((one) => one.employmentId === picked);
      if (!chosen) return undefined;
      setRepin(chosen);
      // Merge as usual: a choice of dates where the entry ends the employment, and none where nothing is ended.
      if (chosen.plan.ends) {
        setStep('dates');
        return undefined;
      }
      return onMerge({ pin: chosen.employmentId });
    };
    return (
      <FormFrame
        host="window"
        open
        title={t('recordChecks.jobMerge.repinTitle')}
        subtitle={row.suggested}
        onCancel={onCancel}
        dataForm="job-merge-repin"
        typed={false}
        act={{ label: t('recordChecks.jobMerge.continue'), onAct: go, ...(picked ? {} : { disabled: true, disabledReason: t('recordChecks.jobMerge.pickFirst') }) }}
      >
        <div className={styles.window} data-job-merge-repin={row.id}>
          <p className={styles.quiet}>{t('recordChecks.jobMerge.repinSays')}</p>
          <RadioGroup
            name={`job-merge-repin-${row.id}`}
            legend={t('recordChecks.jobMerge.repinLegend')}
            options={offered.map((one) => ({ value: one.employmentId, label: fill(t(one.moved ? 'recordChecks.jobMerge.repinMoved' : 'recordChecks.jobMerge.repinAt'), { line: one.line }) }))}
            value={picked}
            onChange={setPicked}
          />
        </div>
      </FormFrame>
    );
  }
  const plan = repin?.plan ?? row.jobMerge;
  if (!plan) return null;
  if (step === 'end' && plan.ends) {
    const month = monthWords(plan.ends.slice(0, 7)) ?? plan.ends;
    return (
      <FormFrame
        host="window"
        open
        title={t('recordChecks.jobMerge.endTitle')}
        subtitle={row.suggested}
        onCancel={onCancel}
        dataForm="job-merge-end"
        typed={false}
        act={{
          label: t('recordChecks.jobMerge.confirm'),
          onAct: () => (ticked ? onMerge({ jobDates: 'Modify', endsEmployment: true, ...pin }) : undefined),
          ...(ticked ? {} : { disabled: true, disabledReason: t('recordChecks.jobMerge.tickFirst') }),
        }}
      >
        <div className={styles.window} data-job-merge-end={row.id}>
          <p className={styles.consequence} data-job-merge-end-says="true">
            {fill(t(retiring ? 'recordChecks.jobMerge.retireSays' : 'recordChecks.jobMerge.endSays'), { company: plan.company, month, day: formatDateOnly(plan.ends, dateFormat) })}
          </p>
          <Checkbox label={fill(t('recordChecks.jobMerge.endTick'), { company: plan.company })} checked={ticked} onChange={setTicked} name={`job-merge-end-${row.id}`} />
        </div>
      </FormFrame>
    );
  }
  const options = [
    { value: 'Keep', label: fill(t('recordChecks.jobMerge.keep'), { line: plan.keeps }) },
    { value: 'Modify', label: fill(t('recordChecks.jobMerge.modify'), { line: plan.modifies ?? plan.keeps }) },
  ];
  const merge = () => {
    if (!dates) return undefined;
    // 🔒 Decision 6442: Modify Dates that ends an open employment goes to its confirm step first.
    if (dates === 'Modify' && plan.ends) {
      setStep('end');
      return undefined;
    }
    return onMerge({ jobDates: dates, ...pin });
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('recordChecks.jobMerge.title')}
      subtitle={row.suggested}
      onCancel={onCancel}
      dataForm="job-merge"
      typed={false}
      act={{ label: t('recordChecks.merge'), onAct: merge, ...(dates ? {} : { disabled: true, disabledReason: t('recordChecks.jobMerge.choose') }) }}
    >
      <div className={styles.window} data-job-merge={row.id}>
        <RadioGroup name={`job-merge-${row.id}`} legend={t('recordChecks.jobMerge.legend')} options={options} value={dates} onChange={(value) => setDates(value as JobMergeDates)} />
        {plan.leftAlone ? <p className={styles.quiet} data-job-merge-left-alone="true">{fill(t('recordChecks.jobMerge.leftAlone'), { months: plan.leftAlone ?? '' })}</p> : null}
        {plan.ends ? <p className={styles.quiet} data-job-merge-ends="true">{fill(t('recordChecks.jobMerge.modifyEnds'), { company: plan.company })}</p> : null}
      </div>
    </FormFrame>
  );
}
